import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import Notification from '@this/src/notification';
import { Fetcher } from '@this/src/util';
import type EanHotelCancelInventoryItem from '@this/src/domain/arrangement/ean_hotel_cancel_inventory_item';
import CancelTableRow from './cancel_table_row';

interface Props {
  items: EanHotelCancelInventoryItem[];
  totalCount: number;
  totalPage: number;
  page: number;
  fetchItems: () => void;
  setPage: (page: number) => void;
}

const CancelResultTable: React.FC<Props> = ({ items, totalCount, totalPage, page, fetchItems, setPage }) => {
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // ページ移動・再検索で非表示になった行のidを一括棚卸に送らないため
  useEffect(() => {
    setSelectedIds([]);
  }, [items]);

  const bulkTargetItems = useMemo(() => items.filter(item => item.bulkInventoriable), [items]);

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? bulkTargetItems.map(item => item.id) : []);
    },
    [bulkTargetItems]
  );

  const handleToggle = useCallback(
    (checked: boolean, id: number) => {
      if (checked) {
        setSelectedIds([...selectedIds, id]);
      } else {
        setSelectedIds(_.without(selectedIds, id));
      }
    },
    [selectedIds]
  );

  const handleBulkInventory = useCallback(async () => {
    if (loading || selectedIds.length === 0) return;

    try {
      setLoading(true);
      await Fetcher.put('/arrangement/auto_reservation_inventory/ean_hotel_cancel/items/bulk_inventory', {
        cancel_request_ids: selectedIds
      });
      setSelectedIds([]);
      fetchItems();
      Notification.success('一括棚卸を実行しました');
    } catch {
      Notification.error('一括棚卸に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [loading, selectedIds, fetchItems]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div>検索結果:{totalCount}件</div>
        <Button onClick={() => handleBulkInventory()} disabled={selectedIds.length === 0} loading={loading}>
          一括棚卸
        </Button>
        {bulkTargetItems.length === 0 && (
          <div style={{ color: 'gray' }}>
            一括棚卸の対象（API=キャンセル済・金額一致・ポリシー算出以外・未棚卸し）がありません
          </div>
        )}
      </div>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                checked={bulkTargetItems.length > 0 && selectedIds.length === bulkTargetItems.length}
                disabled={bulkTargetItems.length === 0}
                onChange={e => handleToggleAll(e.target.checked)}
              />
            </TableCell>
            <TableCell>ステータス(元商品)</TableCell>
            <TableCell>ステータス(キャンセル料商品)</TableCell>
            <TableCell>APIステータス</TableCell>
            <TableCell>EANの旅程番号</TableCell>
            <TableCell>旅程番号</TableCell>
            <TableCell>trace id(元)</TableCell>
            <TableCell>trace id(キャンセル料)</TableCell>
            <TableCell>施設名</TableCell>
            <TableCell>チェックイン日</TableCell>
            <TableCell>チェックアウト日</TableCell>
            <TableCell style={{ minWidth: '180px' }}>宿泊代金・キャンセル料</TableCell>
            <TableCell>右ナビ請求対象日</TableCell>
            <TableCell>キャンセル依頼日</TableCell>
            <TableCell>キャンセル実行者</TableCell>
            <TableCell>キャンセル実行日時</TableCell>
            <TableCell>棚卸ステータス</TableCell>
            <TableCell>棚卸実施者</TableCell>
            <TableCell>棚卸日時</TableCell>
            <TableCell style={{ minWidth: '180px' }}>コメント</TableCell>
            <TableCell>アクション</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <CancelTableRow
              key={item.id}
              item={item}
              checked={selectedIds.includes(item.id)}
              onInventory={fetchItems}
              onToggle={(checked: boolean) => handleToggle(checked, item.id)}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        count={totalPage}
        page={page}
        onChange={(_e: unknown, newPage: number) => setPage(newPage)}
      />
    </>
  );
};

export default CancelResultTable;
